import { ProjectColoredLink } from "@selector/shared/components";
import { type FC, useMemo } from "react";
import {
  Card,
  CardRowSimple,
  CardSection,
  ColoredLabel,
  formatDateTimeFromISO,
  type GridCard,
  LoadingSpinner,
} from "widgets";

import { getEventTagIntent, getTagIntentColor } from "@/app-utils/events.js";
import { useError } from "@/app-utils/graphql/useError.ts";
import { useNotificationSummaryViewUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import type { BlastJob, Playbook, PlaybookEvent, User } from "@/graphql.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.js";
import { getProjectLabel } from "@/redux/modules/config.js";
import { DownloadProcessorLogsButton } from "@/ui-modules/core/DownloadProcessorLogsButton.tsx";
import { EditPlaybookEventButton } from "@/ui-modules/core/EditPlaybookEventButton.tsx";
import { useGetPlaybookEventSettingsCardQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

import { JobEventTile, PlaybookTile } from "../../core/tiles";

export interface IPlaybookEventSettingsCardProps extends GridCard {
  eventId: number;
}

export const PlaybookEventSettingsCard: FC<IPlaybookEventSettingsCardProps> = ({ eventId }) => {
  const { data, loading, error, refetch } = useGetPlaybookEventSettingsCardQuery({
    variables: { eventId: eventId },
  });
  void useError(error);
  useNotificationSummaryViewUpdate(refetch, eventId);
  const playbookEvent = data?.playbook?.event;

  const hasInputData = useMemo(() => {
    const inputs = playbookEvent?.inputs;
    const hasInputValues =
      inputs?.filter((input) => input.inputValues != undefined && input.inputValues.length > 0)?.length ?? 0 > 0;

    return !!(inputs !== undefined && hasInputValues);
  }, [playbookEvent?.inputs]);

  const project = playbookEvent?.project ? (
    <ProjectColoredLink project={playbookEvent?.project} />
  ) : (
    <>You do not have access to this {getProjectLabel(false, false)}.</>
  );

  return (
    <Card
      key="event-info-card"
      title={"Playbook Event Info"}
      actions={[
        <EditPlaybookEventButton
          eventId={eventId}
          key={`edit-playbook-button-on-card`}
          onSaved={refetch}
          eventOwner={playbookEvent?.owner as User}
        />,
        <DownloadProcessorLogsButton
          playbookEvent={playbookEvent as PlaybookEvent}
          key={"download-log-button-on-card"}
        />,
      ]}
    >
      {loading || !playbookEvent ? (
        <LoadingSpinner />
      ) : (
        <>
          <CardRowSimple label={"Playbook"}>
            <PlaybookTile playbook={playbookEvent.playbook as Playbook} />
          </CardRowSimple>
          <CardRowSimple label={"Name"} value={playbookEvent.alias} />
          <CardRowSimple label={"Status"}>
            <ColoredLabel
              color={getTagIntentColor(getEventTagIntent(playbookEvent.status))}
              key={"event-summary-view-tag-2"}
              label={playbookEvent.status}
            />
          </CardRowSimple>
          <CardRowSimple label={"Description"} value={playbookEvent.note} />
          <CardRowSimple label={getProjectLabel(true, false)} value={project} />
          <CardRowSimple label={"Creation date"} value={formatDateTimeFromISO(playbookEvent.created)} />
          <CardRowSimple label={"User"} value={getUserDisplayName(playbookEvent.owner, false, false)} />
          {playbookEvent.workflowName ? <CardRowSimple label={"Workflow"} value={playbookEvent.workflowName} /> : null}
          {playbookEvent.job ? (
            <CardRowSimple label={"Related blast job"}>
              <JobEventTile job={playbookEvent.job as BlastJob} />
            </CardRowSimple>
          ) : null}
          {hasInputData ? (
            <CardSection title={"Settings"}>
              {playbookEvent?.workflowP2ExtendedConfig?.sampleType?.name != null && (
                <CardRowSimple label={"Sample Type"}>
                  {playbookEvent?.workflowP2ExtendedConfig?.sampleType?.name}
                </CardRowSimple>
              )}
              {playbookEvent.inputs?.map((eventInput, index) => (
                <CardRowSimple label={eventInput.label} key={`even-summary-page-inputs-${index}`}>
                  {eventInput.inputValues?.join(", ")}
                </CardRowSimple>
              ))}
            </CardSection>
          ) : null}
        </>
      )}
    </Card>
  );
};
